import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Collapse from '@material-ui/core/Collapse';
import SvgIcon from '@material-ui/core/SvgIcon';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import { TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL } from 'config/urls';
import {
  each,
  every,
  filter,
  find,
  first,
  flatten,
  inRange,
  includes,
  isEmpty,
  isEqual,
  isNil,
  last,
  map,
  union,
  xor
} from 'lodash';

import {
  GridView,
  InfiniteScrollContainer,
  LoadingGridViewSkeleton
} from 'pages/Teachers/StudentPortfolio/components';
import { splitAssignmentsToMonths } from 'utils/assignments';
import { STUDENT_PORTFOLIO_STANDARD_TABS } from 'utils/constants';
import { getStandardSiblings } from 'utils/standards';

import Button from 'components/Button';
import NoResultsPlaceholder from 'components/NoResultsPlaceholder';
import StandardNode from 'components/StandardNode';
import Typography from 'components/Typography';

import styles from './styles.module.scss';

const StandardsView = ({
  student,
  filters,
  initialFilters,
  standards,
  standardsLoading,
  activeStandardTab,
  studentAssignments,
  loadNextPage
}) => {
  const [standardIdsToRender, setStandardIdsToRender] = useState([]);

  useEffect(() => {
    if (!isEmpty(standards)) {
      const standardsToExpand = !isEqual(initialFilters, filters)
        ? standards
        : standards.filter((standard) => inRange(standard.level, 0, 3));

      const initiallyOpenedStandards = map(standardsToExpand, 'id');

      setStandardIdsToRender(initiallyOpenedStandards);
    }
  }, [standards, filters, initialFilters]);

  const monthsAssignments = useMemo(
    () =>
      splitAssignmentsToMonths({
        assignments: studentAssignments.data.results
      }),
    [studentAssignments.data.results]
  );

  const getStandardDirectChildren = useCallback(
    ({ standard }) => filter(standards, { parent_id: standard.id }),
    [standards]
  );

  const transformedStandards = useMemo(
    () =>
      map(standards, (standard) => {
        return {
          ...standard,
          direct_children: getStandardDirectChildren({ standard })
        };
      }),
    [standards, getStandardDirectChildren]
  );

  const getStandardChildren = ({ children, standard }) => {
    const directChildren = getStandardDirectChildren({ standard });

    children.push(directChildren);

    each(directChildren, (child) => {
      getStandardChildren({ children, standard: child });
    });

    return flatten(children);
  };

  const getStandardParent = ({ standard }) =>
    find(transformedStandards, {
      id: standard.parent_id
    });

  const getStandardParents = ({ parents, standard }) => {
    if (standard.parent_id) {
      const parent = getStandardParent({ standard });

      if (parent) {
        parents.push(parent);
      }

      if (parent?.parent_id) {
        getStandardParents({ parents, standard: parent });
      }
    }

    return flatten(parents);
  };

  const parentOnLevelIsOnlyChildOrLastChild = ({ level, standard }) => {
    const standardAllParents = getStandardParents({
      parents: [],
      standard
    });

    const parentOnLevel = first(filter(standardAllParents, { level }));

    if (!parentOnLevel) {
      return false;
    }

    const parentSiblings = getStandardSiblings({
      standard: parentOnLevel,
      standards: transformedStandards
    });

    const parentOnLevelIsOnlyChild = isEmpty(parentSiblings);

    const standardsOnLevel = filter(transformedStandards, {
      level,
      parent_id: parentOnLevel.parent_id
    });

    const parentOnLevelIsLastChild =
      last(standardsOnLevel).id === parentOnLevel.id;

    return parentOnLevelIsOnlyChild || parentOnLevelIsLastChild;
  };

  const closeStandard = ({ standard }) => {
    // Get all standard children down the tree
    const standardsToToggle = getStandardChildren({ children: [], standard });
    const standardIdsToToggle = map(standardsToToggle, 'id');

    setStandardIdsToRender(
      filter(
        standardIdsToRender,
        (standardId) => !includes(standardIdsToToggle, standardId)
      )
    );
  };

  const openStandard = ({ standard }) => {
    // Get all direct standard children
    const standardsToToggle = getStandardDirectChildren({ standard });

    setStandardIdsToRender(
      xor(standardIdsToRender, map(standardsToToggle, 'id'))
    );
  };

  const openStandardAndAllChildren = ({ standard }) => {
    const standardsToToggle = getStandardChildren({ children: [], standard });
    const standardIdsToToggle = map(standardsToToggle, 'id');

    setStandardIdsToRender(union(standardIdsToRender, standardIdsToToggle));
  };

  const noResultsPlaceholder = useMemo(
    () => (
      <NoResultsPlaceholder
        text="to get started"
        button={
          <Button
            color="blue"
            className={styles.captureWorkButton}
            noTypography
            href={TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL}
          >
            <SvgIcon className={styles.cameraIcon} component={CameraAltIcon} />
            <Typography variant="H-TEXT-3">capture work</Typography>
          </Button>
        }
      />
    ),
    []
  );

  return (
    <>
      {standardsLoading && <StandardNode.Placeholder count={3} />}

      {!standardsLoading &&
        isEmpty(transformedStandards) &&
        (activeStandardTab === STUDENT_PORTFOLIO_STANDARD_TABS.ELA ||
          activeStandardTab === STUDENT_PORTFOLIO_STANDARD_TABS.MATH) &&
        noResultsPlaceholder}

      {!standardsLoading &&
        !isNil(student) &&
        !isEmpty(transformedStandards) &&
        (activeStandardTab === STUDENT_PORTFOLIO_STANDARD_TABS.ELA ||
          activeStandardTab === STUDENT_PORTFOLIO_STANDARD_TABS.MATH) && (
          <>
            {map(transformedStandards, (standard) => {
              // A standard is opened if all of their direct children are rendered
              const standardIsOpened = every(
                standard.direct_children,
                (child) => includes(standardIdsToRender, child.id)
              );

              return (
                <Collapse
                  in={includes(standardIdsToRender, standard.id)}
                  key={standard.id}
                >
                  <StandardNode
                    forStudents={false}
                    standard={standard}
                    standards={transformedStandards}
                    opened={standardIsOpened}
                    openStandard={openStandard}
                    closeStandard={closeStandard}
                    openStandardAndAllChildren={openStandardAndAllChildren}
                    parentOnLevelIsOnlyChildOrLastChild={
                      parentOnLevelIsOnlyChildOrLastChild
                    }
                    student={student}
                  />
                </Collapse>
              );
            })}
          </>
        )}

      {studentAssignments.loading &&
        isEmpty(studentAssignments.data.results) &&
        activeStandardTab === STUDENT_PORTFOLIO_STANDARD_TABS.NO_STANDARDS && (
          <div className={styles.gridViewContainer}>
            <LoadingGridViewSkeleton />
          </div>
        )}

      {!studentAssignments.loading &&
        isEmpty(studentAssignments.data.results) &&
        activeStandardTab === STUDENT_PORTFOLIO_STANDARD_TABS.NO_STANDARDS &&
        noResultsPlaceholder}

      {activeStandardTab === STUDENT_PORTFOLIO_STANDARD_TABS.NO_STANDARDS &&
        !isNil(student) && (
          <InfiniteScrollContainer
            length={studentAssignments.data.results.length}
            fetchNext={loadNextPage}
            hasNext={studentAssignments.data.next}
          >
            <div className={styles.gridViewContainer}>
              <GridView
                monthsAssignments={monthsAssignments}
                student={student}
              />
            </div>
          </InfiniteScrollContainer>
        )}
    </>
  );
};

export default StandardsView;
